import { FileText } from 'lucide-react';
import type { ResumeUpdate } from '../../hooks/useAgentSession';

interface ResumePreviewPanelProps {
  updates: ResumeUpdate[];
}

export default function ResumePreviewPanel({ updates }: ResumePreviewPanelProps) {
  if (updates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
        <FileText className="w-12 h-12 mb-3 opacity-30" />
        <p className="text-sm font-medium">Resume Preview</p>
        <p className="text-xs mt-1 text-center">
          Your tailored resume will appear here as the coach works on it.
        </p>
      </div>
    );
  }

  // Group updates by section
  const sections = new Map<string, ResumeUpdate>();
  for (const update of updates) {
    sections.set(update.section, update);
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-800">Tailored Resume</h2>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-auto">
            {updates.length} update{updates.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Summary */}
        {sections.has('summary') && (
          <div className="mb-6 animate-fadeIn">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Summary
            </h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {sections.get('summary')!.content}
              </p>
            </div>
          </div>
        )}

        {/* Experience */}
        {sections.has('experience') && (
          <div className="mb-6 animate-fadeIn">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Experience
            </h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {sections.get('experience')!.content}
              </p>
            </div>
          </div>
        )}

        {/* Skills */}
        {sections.has('skills') && (
          <div className="mb-6 animate-fadeIn">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Skills
            </h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {sections.get('skills')!.content}
              </p>
            </div>
          </div>
        )}

        {/* Title Adjustments */}
        {sections.has('title_adjustments') && (
          <div className="mb-6 animate-fadeIn">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Title Adjustments
            </h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {sections.get('title_adjustments')!.content}
              </p>
            </div>
          </div>
        )}

        {/* Timeline of changes */}
        <div className="mt-8 border-t border-gray-200 pt-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Change History
          </h3>
          <div className="space-y-2">
            {updates.map((update, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                <span className={`w-2 h-2 rounded-full ${
                  update.changeType === 'rewrite' ? 'bg-blue-400' :
                  update.changeType === 'add' ? 'bg-green-400' : 'bg-red-400'
                }`} />
                <span className="capitalize">{update.changeType}</span>
                <span className="text-gray-300">·</span>
                <span className="capitalize">{update.section}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
